import React, { forwardRef, useState } from 'react';
import { RxPerson } from 'react-icons/rx';

import cn from '@/lib/clsxm';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import { TEXT_TYPE } from '@/components/@base/typography/text/type';
import XImage from '@/components/@base/x-image';
import AddQuestionBox from '@/components/app/product/components/questions/add-question';
import Props from '@/components/app/product/components/questions/question-card/type';
import { formatDate } from '@/components/shared/helpers';

import { DATA_CY_PRODUCT_QUESTION_CARD, DATA_CY_REPLY_BUTTON } from '@/constant/cypress';
import ICON_SIZE from '@/constant/icon-size-color';
import imagesList from '@/constant/images-list';
import localization, { pageLevelLocalization } from '@/constant/localization';
import { USER_ROLE } from '@/models/comment.model';
import QuestionModel, { QUESTION_STATUS } from '@/models/question.model';
const { product: productLocalization } = pageLevelLocalization;

const QuestionCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      user,
      createdAt,
      content,
      badge,
      replies,
      status,
      hasReplyButton = true,
      onReplyClick,
      currentUser,
      slug,
      productId,
      onUpdateQuestionList,
    },
    ref,
  ) => {
    const [showReplyBox, setShowReplyBox] = useState<boolean>(false);

    const handleReplyClick = () => {
      onReplyClick && onReplyClick(id);
      setShowReplyBox(!showReplyBox);
    };

    const badgeColorClasses =
      badge === USER_ROLE.VENDOR
        ? 'bg-[#FFF5DE] text-[#FFB616]'
        : badge === USER_ROLE.CUSTOMER
          ? 'bg-[#F0F8FF] text-[#6097F3]'
          : 'bg-[#F4F5F6] text-[#878F9B]';

    return (
      <Container data-cy={DATA_CY_PRODUCT_QUESTION_CARD} ref={ref}>
        <Container center className='justify-start gap-2.5'>
          <Container center className='h-[30px] w-[30px] rounded-full bg-[#f9fafc]'>
            {user?.avatar ? (
              <XImage
                src={user?.avatar.publicLink}
                alt={user?.avatar.title}
                className='rounded-full'
                width={30}
                height={30}
              />
            ) : (
              <RxPerson size={ICON_SIZE.md} className='text-[#878F9B]' />
            )}
          </Container>
          <Text
            type={TEXT_TYPE.SPAN}
            className='text-center text-[#4D4A4A]'
            bold
            size={SIZE_ENUM.MD}
          >
            {user?.displayName}
          </Text>
          <Text type={TEXT_TYPE.SPAN} size={SIZE_ENUM.XS} className='text-[#ADADAD]'>
            {formatDate(createdAt)}
          </Text>
          {badge && localization[badge] && status === QUESTION_STATUS.ACCEPTED && (
            <Text
              type={TEXT_TYPE.SPAN}
              size={SIZE_ENUM.XS}
              bold
              className={cn('truncate rounded-md px-2 leading-5', badgeColorClasses)}
            >
              {localization[badge]}
            </Text>
          )}

          {status === QUESTION_STATUS.PENDING && (
            <Text
              type={TEXT_TYPE.SPAN}
              size={SIZE_ENUM.XS}
              bold
              className={cn('truncate rounded-md px-2 leading-5', badgeColorClasses)}
            >
              {productLocalization.pendingComment}
            </Text>
          )}
        </Container>
        <Container
          className='py-2 text-[14px] text-[#5B5C60] md:pb-[13px]'
          dangerouslySetInnerHTML={{ __html: content }}
        ></Container>

        {hasReplyButton && (
          <Button
            data-cy={DATA_CY_REPLY_BUTTON}
            Icon={<XImage src={imagesList.icons.replyIcon} alt={productLocalization.reply} />}
            className='h-8 border-[1px] border-[#E5E8EB] bg-[#FFFFFF] px-3 py-1 text-[#76767C] transition duration-300 hover:bg-[#E5E8EB]'
            onClick={handleReplyClick}
          >
            {productLocalization.reply}
          </Button>
        )}
        {showReplyBox && !!currentUser && slug && productId && (
          <AddQuestionBox
            user={currentUser}
            slug={slug}
            productId={productId}
            onUpdateQuestionList={(question: QuestionModel) => {
              setShowReplyBox(false);
              onUpdateQuestionList && onUpdateQuestionList(question);
            }}
            parentId={id}
            focusQuestionBox={true}
          />
        )}

        {replies && replies.length > 0 && (
          <Container className={cn('pt-8 pr-8 md:pt-12 md:pr-12', !hasReplyButton && 'pt-6')}>
            {replies?.map((reply) => (
              <Container key={reply.id} className='relative mt-6 first:mt-0'>
                <XImage
                  src={imagesList.icons.commentsTreeBranchIcon}
                  alt={productLocalization.reply}
                  width={15}
                  height={40}
                  className='absolute -top-6 -right-8'
                />
                <QuestionCard
                  {...reply}
                  onReplyClick={onReplyClick}
                  hasReplyButton={hasReplyButton}
                  slug={slug}
                  productId={productId}
                  onUpdateQuestionList={onUpdateQuestionList}
                  currentUser={currentUser}
                />
              </Container>
            ))}
          </Container>
        )}
      </Container>
    );
  },
);

export default QuestionCard;
